/* MIXPANEL TRACKER   */

import mixpanel from "mixpanel-browser";
import { clientConfig } from "../config";
import * as http from "../utils/http";

import { setCustomData } from "./KameleoonTracker";

import { getCountryFromUrl } from "../utils/translator";
import { retireClosedAbTests } from "../utils/retiredAbTests";


const ignoreList = ["Placed Order", "ViewThankyouPage"]; //dont send those events to mp, some are already sent through PageTracker

// Global flag to track if Mixpanel initialization is complete
let isMixpanelInitialized = false;

// Maximum number of attempts to wait for initialization
const MAX_INIT_ATTEMPTS = 5;
const MAX_UTMS_RETRY_ATTEMPTS = 3;

// Mixpanel last-touch super property -> Kameleoon custom data (HL-12604), same values.
const KAMELEOON_TRAFFIC_DATA = {
  "Last_touch UTM Source": "utm_source",
  "Last_touch UTM Medium": "utm_medium",
  "Last_touch UTM Campaign": "utm_campaign",
  "Last_touch Campaign id": "campaign_id",
  "Last_touch Utm Content": "ad_name",
  "Last_touch Ad id": "ad_id",
};

const initLastTouchProperties = () => {
  const utmParametersMap = {
    tr: "Click ID",
    utm_source: "UTM Source",
    utm_medium: "UTM Medium",
    utm_campaign: "UTM Campaign",
    cmp_id: "Campaign id",
    grp_id: "Ad group id",
    utm_content: "Utm Content",
    ad_id: "Ad id",
    utm_term: "Utm Term",
  };

  // init utms with empty string, in order to lock the first attributions
  let utms = {};
  let utmsLastTouch = {};
  for (const [value] of Object.entries(utmParametersMap)) {
    utms[value] = "";
    utmsLastTouch[value] = "";
  }

  try {
    let trackingData = window.eventsTracking.getData();
    let lastArrival = trackingData.arrivals[trackingData.arrivals.length - 1];

    let url;

    // in case url starts only with ? -> in case of short arrival (iphone prevent localstorage cross subdomains)
    if (lastArrival.url.startsWith("?")) {
      url = new URL("http://a.com" + lastArrival.url);
    } else {
      url = new URL(lastArrival.url);
    }

    let currAfValue;
    let oldAfValue;
    let cachedAffiliateName;

    url.searchParams.forEach((value, key) => {
      if (key === "af") {
        currAfValue = value;
      }
      else{
        let propertyName = utmParametersMap[key];
        if (propertyName) {
          utms[propertyName] = "Affiliate " + value;
          utmsLastTouch["Last_touch " + propertyName] = value;
        }
      }
    });

    let mixpanelCookie = window.eventsStorage.getFromCookie(
      `mp_${clientConfig.MIXPANEL_TOKEN}_mixpanel`
    );

    if (mixpanelCookie) {
      let mixPanelValues = JSON.parse(mixpanelCookie);

      oldAfValue = mixPanelValues["Last_touch Affiliate ID"];
      cachedAffiliateName = mixPanelValues["Last_touch Affiliate ID Name"];
    }

    // get from server affiliate name if:
    // there is no af stored in mixpanel
    // the current af in mixpanel is different than current
    if (!oldAfValue || oldAfValue !== currAfValue) {

      //get the affiliate name from the server
      http
        .postToServer(`t/init`, { trackingData: window.eventsTracking.getData() }, [], { retry: MAX_UTMS_RETRY_ATTEMPTS })
        .then((response) => {
          mixpanel.register({
            "Last_touch Affiliate ID Name": response.data.affiliateName,
            "Last_touch Affiliate ID" : response.data.externalID
          });
          mixpanel.register({
            "Affiliate Name": response.data.affiliateName,
            "Affiliate ID": response.data.externalID
          });
          if (response.data.affiliateName) setCustomData("affiliate", response.data.affiliateName);

          isMixpanelInitialized = true;

          //store also affiliates cookie for php 
          let currAffCookie = window.eventsStorage.getFromCookie('dtr_aff')

          if (!currAffCookie){
            // disabled because the php uses affiliation data by customer_affiliations table
            // window.eventsStorage.addToCookie('dtr_aff', response.data.phpAffCookie)
          }

        })
        .catch((error) => {
          // setShowStickyNotification(true);
          window.clientLogger.error("error get affiliateName: ", error);
          // Mark as initialized even on error to prevent infinite waiting
          isMixpanelInitialized = true;
        });
    } else {
      // If HTTP request is not needed, mark as initialized immediately
      isMixpanelInitialized = true;
      if (cachedAffiliateName) setCustomData("affiliate", cachedAffiliateName);
    }

    mixpanel.register(utmsLastTouch);
    Object.entries(KAMELEOON_TRAFFIC_DATA).forEach(([mpName, name]) => {
      if (utmsLastTouch[mpName]) setCustomData(name, utmsLastTouch[mpName]);
    });
    mixpanel.register_once(utms);
  } catch (err) {
    window.clientLogger.error("error loading arrival Variables into Mixpanel", err);
  }
};

// Minimal implementation: Just add these two config flags for Session Replay + Heatmaps
// Experiments work automatically - no config needed
if (clientConfig.MIXPANEL_TOKEN) {
  mixpanel.init(clientConfig.MIXPANEL_TOKEN, {
    api_host: clientConfig.MIXPANEL_HOST,
    set_config: {
      "Content-Type": "application/json",
    },
    // Enable Session Replay - records 8% of sessions
    record_sessions_percent: 8,
    // Enable Heatmaps - captures click, scroll, and interaction data
    record_heatmap_data: true,
    // Mixpanel automatically masks all inputs/text/images by default for privacy
    record_mask_text_selector: '',  // Empty string unmasks all static text- Input fields, textareas, and passwords remain automatically masked
    // Experiments are built-in and work automatically
    record_idle_timeout_ms: 900000, // 15 min of inactivity
    record_max_ms: 900000 // 15 min session max
  });
  window.mixpanel = mixpanel;
}

// HL-12264: the market on every event, as the CMS reports it ("UK" for gb/uk, else the country code).
const toMixpanelCountry = (country) => (["gb", "uk"].includes(country.toLowerCase()) ? "UK" : country.toUpperCase());

export const init = () => {

  if (window.eventsTracking && window.eventsTracking.getData("uid")){
    mixpanel.register({uid:window.eventsTracking.getData("uid")})
  }

  const country = getCountryFromUrl(window.location.pathname);
  if (country) mixpanel.register({ country: toMixpanelCountry(country) });

  // HL-12595: drop the buckets + super-properties closed A/B tests left in this browser.
  retireClosedAbTests(clientConfig.MIXPANEL_TOKEN ? mixpanel : null);

  initLastTouchProperties();
};

export const identify = (uuid) => {
  clientConfig.MIXPANEL_TOKEN && mixpanel.identify(uuid);
};

export const alias = (id) => {
  clientConfig.MIXPANEL_TOKEN && mixpanel.alias(id);
};

export const superProperties = (props) => {
  clientConfig.MIXPANEL_TOKEN && mixpanel.register(props);
  window.eventsTracking.setMetaData(props)
};

// Removes super properties registered above. The events tracker has no unset, so the keys are blanked
// there (undefined drops out of the JSON it sends).
export const unregisterSuperProperties = (names) => {
  clientConfig.MIXPANEL_TOKEN && names.forEach((name) => mixpanel.unregister(name));
  window.eventsTracking.setMetaData(Object.fromEntries(names.map((name) => [name, undefined])))
};

export const track = async (eventType, eventName, props) => {  
  if (!ignoreList.includes(eventName)){
    if (!props){
      props = {}
    }
    // Wait for Mixpanel initialization with retry mechanism
    let attempts = 0;
    while (!isMixpanelInitialized && attempts < MAX_INIT_ATTEMPTS) {
      await new Promise(resolve => setTimeout(resolve, 1000)); 
      attempts++;
    }

    // Only track if initialized
    if (isMixpanelInitialized) {
      // send to mixpanel
      clientConfig.MIXPANEL_TOKEN && mixpanel.track(eventName, props);

      // send to our tracking
      window.eventsTracking.track({e:eventName,p:props});

      if (eventType === "pv"){
        let pageName = eventName.replace("View ","").replace(" Page","")
        clientConfig.MIXPANEL_TOKEN && mixpanel.track_pageview({...props, "page_type": pageName});
        window.eventsTracking.track({e:'pv',p:{...props,"page_type": pageName}});
      }
    }
  }
};

export const setPeople = (props) => {
  clientConfig.MIXPANEL_TOKEN && mixpanel.people.set(props);
};
